<template>
  <div
    id="user-notes"
    class="full-height">

    <div class="user-details-box full-height">
      <ScrollArea
        ref="notesScrollbar"
        class="notes-scroll-area"
        @bottom="loadNextPage">
        <h3>Contact notes</h3>
        <transition-group
          v-if="!loadingContact && initialLoadFinished"
          class="user-notes"
          name="remove-note"
          tag="div">
          <div
            v-for="(note, index) in notes.data"
            :key="note.animationKey"
            class="note-item"
            :class="{'editor-state': showEditorForNote >= 0 && index === showEditorForNote}"
            @click="focusEdit(index)">
            <div class="flex justify-start flex-align-center bottom-10 w-100">
              <SBAvatar
                v-if="note.created_by"
                class="avatar greedy"
                size="custom"
                :character-count="1"
                :show-client="true"
                :person="members[note.created_by ? note.created_by.id : null]"
                :hide-avatar-image="false"
                :show-name="true" />
              <span
                v-else
                class="avatar greedy" />
              <p
                v-if="note.created_at"
                class="no-shrink time">{{ note.timeDiff }}</p>
            </div>
            <span
              :ref="`note-${index}`"
              :contenteditable="showEditorForNote >= 0 && index === showEditorForNote"
              class="note-content"
              @input="checkNoteContentEmpty" >{{ note.content }}</span>
            <!-- Note header actions -->
            <span
              class="actions-header"
              v-if="showEditorForNote === null && !cancelingNoteEdit">
              <SBIcon
                :icon="SBIconList.PENCIL"
                :color="SBColors.blackUltralight"
                :size="12"
                :hoverColor="SBColors.greyChatBubble"
                :hoverSize="24"
                :hoverIconColor="SBColors.blackPrimary"
                class="ml-2"
                @click="editNote(index)"
                @click.native.stop />
              <SBIcon
                :icon="SBIconList.TRASH"
                :color="SBColors.blackUltralight"
                :size="12"
                :hoverColor="SBColors.greyChatBubble"
                :hoverSize="24"
                :hoverIconColor="SBColors.blackPrimary"
                class="delete-btn"
                @click="deleteNote(note)" />
            </span>
            <!-- Note footer actions -->
            <transition name="footer-actions">
              <span
                v-if="showEditorForNote >= 0 && index === showEditorForNote"
                class="footer-actions">
                <span
                  class="cancel-btn"
                  @click="cancelEditNote(index)">Cancel</span>
                <span
                  class="save-btn"
                  :class="{disabled: noteContentEmpty}"
                  @click="saveEditNote(index)">Save</span>
              </span>
            </transition>
          </div>
        </transition-group>
        <Spinner v-if="loadingNotes || loadingContact"/>
      </ScrollArea>
      <b-button
        v-if="!loadingContact && initialLoadFinished"
        class="add-note"
        :disabled="inEditorMode"
        @click="addNote">
        <SBIcon
          :icon="SBIconList.PLUS"
          :color="SBColors.blackPrimary"
          :size="8" />
        Create note
      </b-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#user-notes {
  width: $detailsSidebarWidth;
}
.user-details-box {

  .notes-scroll-area {
    height: unset !important;

    .user-notes {
      .note-item {
        margin: 0 15px 15px;
        padding: 15px 12px;
        border-radius: 5px;
        border: solid 1px $greyBorderModal;
        background-color: $white;
        overflow: hidden;
        position: relative;
        transition: all .25s;
        &.no-translateX {
          max-height: 0 !important;
          padding-top: 0;
          padding-bottom: 0;
          margin-bottom: 0;
          opacity: 0;
        }

        &.editor-state {
          padding-bottom: 46px;
        }

        span {
          font-size: 14px !important;
          line-height: 1.2 !important;
          color: $blackPrimary !important;

          &.note-content {
            display: inline-block;
            min-width: 100%;
            outline: none;
            white-space: pre-wrap;
          }
        }

        &:hover .actions-header {
          display: flex;
          opacity: 1;
        }

        .actions-header {
          position: absolute;
          display: flex;
          top: 0;
          right: 0;
          background: $white;
          margin: 11px;
          box-shadow: 0 0 20px 20px $white;
          opacity: 0;
          transition: all .2s;
          transition-delay: .1s;
        }

        .footer-actions {
          display: block;
          position: absolute;
          right: 0;
          bottom: 0;
          padding: 11px;

          span {
            display: inline-block;
            padding: 4px;
            cursor: pointer;

            &.cancel-btn {
              color: $blackUltralight !important;

              &:hover {
                color: $redPrimary !important;
              }
            }

            &.save-btn {
              color: $blackPrimary !important;
              font-weight: 500;

              &:hover {
                color: $bluePrimary !important;
              }

              &.disabled {
                color: $blackUltralight !important;
                opacity: 0.6;
                cursor: default;
              }
            }
          }
        }

        .footer-actions-enter {
          // right: -100%;
          transform: translateY(100%);
        }

        .footer-actions-enter-active, .footer-actions-leave-active {
          transition: all .25s;
        }

        .footer-actions-leave-to {
          // right: -100%;
          transform: translateY(100%);
        }

        &.remove-note-leave-active {
          transition: all .25s;
        }

        &.remove-note-leave-to {
          &:not(.no-translateX) {
            transform: translateX(150%);
          }
        }

        .time {
          font-size: 12px;
          color: $blackUltralight;
          margin-left: 10px;
          font-weight: 500;
        }
      }
    }
  }

  .add-note {
    display: flex;
    padding: 7px 16px;
    margin: 15px auto;
    border-radius: 5px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;
    background-color: $white;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    color: $blackPrimary;

    .sb-icon {
      margin: 0 6px 0 0;
    }

    &:enabled:hover {
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
    }

    &:disabled {
      opacity: 1;
      cursor: default;
    }
  }
}

.bottom-10 {
  margin: 0 0 10px 0;
}

.delete-btn {
  margin-left: 2px;
}

::v-deep {
  .sb-avatar {

    .logo {
      &.custom {
        width: 16px !important;
        height: 16px !important;
        font-size: 8px !important;
        line-height: 16px !important;
        .indicator {
          width: 5px;
          height: 5px;
        }
        .avatar-placeholder {
          mask-size: 62% 62%;
        }
      }
    }

    .data {
      margin: 0 0 0 4px;

      .title {
        font-size: 14px;
        font-weight: 400;
        color: $blackPrimary;
        margin: 0;
        min-height: auto !important;
      }
    }
  }
}
</style>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import placeCursorAtEnd from '@/helpers/placeCursorAtEnd';
import randomString from '@/helpers/randomString';
import Spinner from '../../../common/Spinner';
import ScrollArea from '../../../common/ScrollArea';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import ToasterMixin from '@/mixins/Toaster';
import SBAvatar from '../../../common/Avatar';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'UserNotes',
  components: {
    Spinner,
    ScrollArea,
    SBIcon,
    SBAvatar,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      notesPropertiesRegex: new RegExp('Received e-mail', 'g'),
      showEditorForNote: null,
      previousNoteValue: null,
      nonNoteItems: [],
      noteContentEmpty: true,
      // Flag that indicates the cancel animation is in progress so the note action buttons are not flashed for a moment while mouse is hovering the closing note.
      cancelingNoteEdit: false,
      notesPage: 1,
      noteTimeDiffInterval: null,
      initialLoadFinished: false,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      contact: 'contact/contact',
      loadingContact: 'contact/loadingContact',
      notes: 'contactDetails/notes',
      loadingNotes: 'contactDetails/loadingNotes',
      members: 'organisation/currentOrganisationMembersLookup',
    }),
    inEditorMode() {
      return this.showEditorForNote != null;
    },
    organisationId() {
      return this.$route.params.organisationId;
    },
  },
  watch: {
    activeConversation(changedContact, oldContact) {
      if (changedContact.id !== oldContact.id) {
        this.initialLoadFinished = false;
        this.showEditorForNote = null;
        this.previousNoteValue = null;
        this.noteContentEmpty = true;
        this.cancelingNoteEdit = false;
        this.clearContactNotes();
        this.notesPage = 1;
        this.getContactNotes({
          organisationId: this.organisationId,
          contactId: this.activeConversation.contact.id,
        }).then(() => {
          this.initialLoadFinished = true;
        });
      }

      this.NOTES_VIEW_ACTIVE_FOR_CONTACT(changedContact.id);
    },
    notes: {
      deep: true,
      handler() {
        this.calculateTimeDiffs();
        this.$nextTick(() => {
          this.loadMoreIfNeeded();
        });
      },
    },
  },
  mounted() {
    this.getContactNotes({
      organisationId: this.organisationId,
      contactId: this.activeConversation.contact.id,
    }).then(() => {
      this.initialLoadFinished = true;
    });

    this.NOTES_VIEW_ACTIVE_FOR_CONTACT(this.activeConversation.contact.id);

    this.noteTimeDiffInterval = setInterval(this.calculateTimeDiffs, 60000);
  },
  beforeDestroy() {
    this.NOTES_VIEW_ACTIVE_FOR_CONTACT(null);
    clearInterval(this.noteTimeDiffInterval);
    this.clearContactNotes();
    this.notesPage = 1;
  },
  methods: {
    ...mapActions({
      syncContactNotes: 'contact/syncContactNotes',
      getContactNotes: 'contactDetails/getContactNotes',
      clearContactNotes: 'contactDetails/clearContactNotes',
      addNoteVuex: 'contactDetails/addNote',
      deleteNoteVuex: 'contactDetails/deleteNote',
      undeleteVuexNote: 'contactDetails/undeleteNote',
      saveNoteVuex: 'contactDetails/saveNote',
    }),
    ...mapMutations({
      NOTES_VIEW_ACTIVE_FOR_CONTACT: 'contactDetails/NOTES_VIEW_ACTIVE_FOR_CONTACT',
    }),
    loadMoreIfNeeded() {
      if (!this.$refs.notesScrollbar.verticalScrollActive) {
        this.loadNextPage();
      }
    },
    loadNextPage() {
      if (this.notes.meta.last_page > this.notesPage) {
        this.getContactNotes({
          organisationId: this.organisationId,
          contactId: this.activeConversation.contact.id,
          data: {
            page: ++this.notesPage,
          },
        });
      }
    },
    addNote() {
      this.addNoteVuex();
      this.$nextTick(() => {
        this.editNote();
      });
    },
    checkNoteContentEmpty() {
      this.noteContentEmpty = !!(this.showEditorForNote === null || this.$refs[`note-${this.showEditorForNote}`][0].innerText.length === 0);
    },
    editNote(index = this.notes.data.length - 1) {
      this.showEditorForNote = index;
      this.previousNoteValue = this.$refs[`note-${index}`] && this.$refs[`note-${index}`][0] ? this.$refs[`note-${index}`][0].innerText : '';
      this.checkNoteContentEmpty();
      this.$nextTick(() => {
        this.$refs[`note-${index}`][0].innerText = this.previousNoteValue;
        placeCursorAtEnd(this.$refs[`note-${index}`][0]);
      });
    },
    cancelEditNote(index) {
      this.cancelingNoteEdit = true;
      this.showEditorForNote = null;

      // If no previous value then this is a new note
      if (!this.previousNoteValue) {
        const parentHeight = this.$refs[`note-${index}`][0].offsetParent.offsetHeight + 10; // +10 just in case and it wont do any noticable difference on the animation
        // set max-height so the cancel animation can go smoothly
        this.$refs[`note-${index}`][0].offsetParent.style.maxHeight = `${parentHeight}px`;

        this.$nextTick(() => {
          // When canceling new note do not show the "move to right" animation.
          this.$refs[`note-${index}`][0].parentElement.classList.add('no-translateX');
          this.deleteNote(this.notes.data[index]);
        });
      } else {
        // In case this was an edit of an already existing note then just apply original value
        this.$refs[`note-${index}`][0].innerText = this.previousNoteValue;
        this.previousNoteValue = null;
      }
      // Cancel animation is 250ms. Additional 50ms added just in case.
      setTimeout(() => { this.cancelingNoteEdit = false; }, 300);
    },
    saveEditNote(index) {
      const previousNoteValueBackup = this.previousNoteValue;

      if (!this.noteContentEmpty) {
        this.showEditorForNote = null;
        this.previousNoteValue = null;
        this.$nextTick(() => {
          if (this.$refs[`note-${index}`][0].innerText.trim() === '') {
            this.deleteNote(this.notes.data[index]);
          }
          this.saveNoteVuex({
            organisationId: this.organisationId,
            contactId: this.activeConversation.contact.id,
            index,
            id: this.notes.data[index].id,
            content: this.$refs[`note-${index}`][0].innerText,
          })
            .catch(() => {
              if (this.checkPermission('can_edit_contact')) {
                this.showToaster('Saving note failed. Please try again.', null, null, 'error');
              } else {
                this.showToaster('You don’t have a permission to do this action.', null, null, 'warning');
              }
              this.showEditorForNote = index;
              this.previousNoteValue = previousNoteValueBackup;
            });
        });
      }
    },
    deleteNote(targetNote) {
      if (targetNote.id) {
        this.deleteNoteById(targetNote.id);
      } else {
        this.deleteLastNote();
      }
    },
    deleteNoteById(id) {
      const noteIndex = this.notes.data.findIndex(note => note.id === id);
      const noteBkp = JSON.parse(JSON.stringify(this.notes.data[noteIndex]));
      this.deleteNoteVuex({
        organisationId: this.organisationId,
        contactId: this.activeConversation.contact.id,
        index: noteIndex,
        id,
        data: {
          noCancel: true,
        },
      })
        .catch(() => {
          // Deleting of the note failed
          this.showToaster('Deleting note failed. Please try again.', null, null, 'error');
          this.undeleteVuexNote({ index: noteIndex, noteBkp });
        });
    },
    deleteLastNote() {
      this.deleteNoteVuex({
        index: this.notes.data.length,
      });
    },
    focusEdit(index) {
      if (index === this.showEditorForNote) {
        this.$nextTick(() => {
          placeCursorAtEnd(this.$refs[`note-${index}`][0]);
        });
      }
    },
    calculateTimeDiffs() {
      this.notes.data.forEach((note) => {
        let value = '';
        if (note.created_at) {
          const daysDiff = this.$moment().diff(this.$moment(false, false).unix(note.created_at), 'days');
          const isSameYear = this.$moment(false, false).unix(note.created_at).year() === this.$moment().year();
          if (daysDiff < 2) {
            value = this.$moment(false, false).unix(note.created_at).fromNow(true);
          } else if (daysDiff < 31) {
            value = `${daysDiff}d`;
          } else if (isSameYear) {
            value = this.$moment(false, false).unix(note.created_at).format('D MMM');
          } else if (!isSameYear) {
            value = this.$moment(false, false).unix(note.created_at).format('MMM YYYY');
          }
        }

        this.$set(note, 'timeDiff', value);
      });
    },
  },
};
</script>
